import type { PatrolPeriodIso } from "./normalizePatrolWindow";

/** Treat as a single instant — common when the API omits a real window start. */
const COLLAPSE_EPS_MS = 2_000;

function isDegenerateWindow(startMs: number, endMs: number): boolean {
  return (
    Number.isNaN(startMs) ||
    Number.isNaN(endMs) ||
    startMs > endMs ||
    Math.abs(endMs - startMs) < COLLAPSE_EPS_MS
  );
}

/**
 * Human-readable patrol interval: obligation start (shift start or last patrol / server anchor)
 * through the deadline. Deadline always comes from normalized `window_end`.
 */
export function resolvePatrolDisplayPeriod(options: {
  normalized: PatrolPeriodIso | null;
  openShift: { started_at: string } | null | undefined;
  /** Optional API field: when the current obligation window began (e.g. after last scan). */
  periodAnchorIso?: string | null;
  /** When there is no open shift and bounds are degenerate, estimate start from interval length. */
  fallbackIntervalMinutes?: number | null;
}): { displayStartIso: string; displayEndIso: string } | null {
  const { normalized, openShift, periodAnchorIso, fallbackIntervalMinutes } = options;
  if (!normalized?.periodEndIso) return null;

  const endMs = new Date(normalized.periodEndIso).getTime();
  if (Number.isNaN(endMs)) return null;

  const anchorSource = periodAnchorIso?.trim() || normalized.periodStartIso;
  const rawStartMs = anchorSource ? new Date(anchorSource).getTime() : NaN;
  const shiftMs =
    openShift?.started_at != null ? new Date(openShift.started_at).getTime() : NaN;
  const degenerate = isDegenerateWindow(rawStartMs, endMs);

  let startMs: number;

  if (openShift != null && !Number.isNaN(shiftMs)) {
    if (degenerate) {
      startMs = shiftMs;
    } else {
      startMs = Math.max(rawStartMs, shiftMs);
    }
  } else if (degenerate && fallbackIntervalMinutes != null && fallbackIntervalMinutes > 0) {
    startMs = endMs - fallbackIntervalMinutes * 60_000;
  } else if (!degenerate && !Number.isNaN(rawStartMs)) {
    startMs = rawStartMs;
  } else if (!Number.isNaN(rawStartMs)) {
    startMs = rawStartMs;
  } else {
    startMs = endMs;
  }

  if (startMs >= endMs) {
    if (openShift != null && !Number.isNaN(shiftMs) && shiftMs < endMs) {
      startMs = shiftMs;
    } else if (fallbackIntervalMinutes != null && fallbackIntervalMinutes > 0) {
      startMs = endMs - fallbackIntervalMinutes * 60_000;
    } else {
      startMs = endMs - 60_000;
    }
  }

  return {
    displayStartIso: new Date(startMs).toISOString(),
    displayEndIso: normalized.periodEndIso,
  };
}
